import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/EmptyState';
import { APPOINTMENT_STATES } from '../utils/mockData';
import {
  BarChart2,
  RefreshCw,
  CalendarDays,
  CheckCircle2,
  Clock,
  XCircle,
  UserX,
  Users,
  TrendingUp,
  FileDown,
  Sheet,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { API } from '@/lib/api';

const fmt = (d) => d.toISOString().slice(0, 10);

function defaultRange() {
  const end   = new Date();
  const start = new Date(end.getFullYear(), end.getMonth(), 1);
  return { inicio: fmt(start), fin: fmt(end) };
}

/* ── KPI Card ─────────────────────────────────────────────────── */
function KpiCard({ icon: Icon, label, value, sub, colorClass }) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start gap-3">
          <div className={cn('p-2 rounded-lg shrink-0 mt-0.5', colorClass)}>
            <Icon className="h-4 w-4" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold leading-tight">{value}</p>
            {sub != null && (
              <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ── Main Page ────────────────────────────────────────────────── */
export const ReportsPage = () => {
  const [range,       setRange]       = useState(defaultRange());
  const [doctors,     setDoctors]     = useState([]);
  const [selectedDoc, setSelectedDoc] = useState('');
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [clinicName,  setClinicName]  = useState('Clínica');

  // El token lo inyecta axios globalmente (AuthContext), no hace falta pasarlo a mano.
  useEffect(() => {
    axios.get(`${API}/config`)
      .then(r => { if (r.data.clinic_name) setClinicName(r.data.clinic_name); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    axios.get(`${API}/doctors`)
      .then(r => setDoctors(r.data))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReport = useCallback(async () => {
    if (!range.inicio || !range.fin) return;
    setLoading(true);
    const params = { fecha_inicio: range.inicio, fecha_fin: range.fin };
    if (selectedDoc) params.doctor_id = selectedDoc;
    try {
      const r = await axios.get(`${API}/reports/summary`, { params });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cargar reporte');
    } finally {
      setLoading(false);
    }
  }, [range, selectedDoc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load on mount
  useEffect(() => { loadReport(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pct = (n) => data?.total ? Math.round((n / data.total) * 100) : 0;

  const exportPDF = async () => {
    const { default: jsPDF } = await import('jspdf');
    await import('jspdf-autotable');
    const doc = new jsPDF();
    const fileName = `reporte_${range.inicio}_${range.fin}.pdf`;

    // Encabezado
    doc.setFillColor(14, 165, 233); // cyan-500
    doc.rect(0, 0, 210, 18, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(clinicName, 10, 12);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Período: ${range.inicio} – ${range.fin}`, 140, 12);
    doc.setTextColor(0, 0, 0);

    // KPIs
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumen de citas', 10, 28);
    doc.autoTable({
      startY: 32,
      head: [['Métrica', 'Total']],
      body: [
        ['Total citas', data.total],
        ['Confirmadas', data.por_estado.confirmada],
        ['Atendidas', data.por_estado.atendido],
        ['Canceladas', data.por_estado.cancelada],
        ['No asistió', data.por_estado.no_asistio],
      ],
      styles: { fontSize: 9 },
      headStyles: { fillColor: [14, 165, 233] },
    });

    // Citas por Día
    if (data.por_dia?.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.text('Citas por Día', 10, doc.lastAutoTable.finalY + 10);
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 14,
        head: [['Fecha', 'Citas']],
        body: data.por_dia.map(r => [r.fecha, r.count]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [14, 165, 233] },
      });
    }

    // Top Doctores
    if (data.top_doctores.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.text('Top Doctores', 10, doc.lastAutoTable.finalY + 10);
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 14,
        head: [['Doctor', 'Citas', '%']],
        body: data.top_doctores.map(d => [d.nombre, d.total, `${pct(d.total)}%`]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [14, 165, 233] },
      });
    }

    doc.save(fileName);
    toast.success('PDF exportado');
  };

  const exportExcel = async () => {
    const XLSX = await import('xlsx');
    const wb = XLSX.utils.book_new();

    // Hoja Resumen
    const resumenData = [
      ['Métrica', 'Total'],
      ['Total citas', data.total],
      ['Confirmadas', data.por_estado.confirmada],
      ['Atendidas', data.por_estado.atendido],
      ['Canceladas', data.por_estado.cancelada],
      ['No asistió', data.por_estado.no_asistio],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resumenData), 'Resumen');

    // Hoja Por Día
    if (data.por_dia?.length > 0) {
      const porDiaData = [['Fecha', 'Citas'], ...data.por_dia.map(r => [r.fecha, r.count])];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(porDiaData), 'Por Día');
    }

    // Hoja Top Doctores
    if (data.top_doctores.length > 0) {
      const doctoresData = [
        ['Doctor', 'Citas'],
        ...data.top_doctores.map(d => [d.nombre, d.total]),
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(doctoresData), 'Top Doctores');
    }

    XLSX.writeFile(wb, `reporte_${range.inicio}_${range.fin}.xlsx`);
    toast.success('Excel exportado');
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl mx-auto">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart2 className="h-6 w-6" />
            Reportes
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Resumen estadístico de citas por período
          </p>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-1">
                <Label htmlFor="r-inicio">Desde</Label>
                <Input
                  id="r-inicio"
                  type="date"
                  className="h-9 w-40"
                  value={range.inicio}
                  onChange={e => setRange(r => ({ ...r, inicio: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="r-fin">Hasta</Label>
                <Input
                  id="r-fin"
                  type="date"
                  className="h-9 w-40"
                  value={range.fin}
                  onChange={e => setRange(r => ({ ...r, fin: e.target.value }))}
                />
              </div>
              {doctors.length > 0 && (
                <div className="space-y-1">
                  <Label htmlFor="r-doctor">Doctor</Label>
                  <select
                    id="r-doctor"
                    className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                    value={selectedDoc}
                    onChange={e => setSelectedDoc(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {doctors.map(d => (
                      <option key={d.id} value={d.id}>{d.nombre}</option>
                    ))}
                  </select>
                </div>
              )}
              <Button onClick={loadReport} disabled={loading} size="sm">
                <RefreshCw className={cn('h-4 w-4 mr-1', loading && 'animate-spin')} />
                Generar
              </Button>
              {data && (
                <>
                  <Button variant="outline" size="sm" onClick={exportPDF}>
                    <FileDown className="h-4 w-4 mr-1" />
                    PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={exportExcel}>
                    <Sheet className="h-4 w-4 mr-1" />
                    Excel
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {loading && !data ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-24 rounded-xl" />
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Skeleton className="h-48 rounded-xl" />
              <Skeleton className="h-48 rounded-xl" />
            </div>
          </div>
        ) : !data ? (
          <EmptyState
            icon={BarChart2}
            title="Genera tu primer reporte"
            description="Elige un rango de fechas (y un doctor si quieres) y presiona Generar."
          />
        ) : (
          <>
            {/* KPI Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <KpiCard
                icon={CalendarDays}
                label="Total citas"
                value={data.total}
                colorClass="bg-slate-100 text-slate-700"
              />
              <KpiCard
                icon={CheckCircle2}
                label="Confirmadas"
                value={data.por_estado.confirmada}
                sub={`${pct(data.por_estado.confirmada)}%`}
                colorClass="bg-blue-100 text-blue-700"
              />
              <KpiCard
                icon={Clock}
                label="Atendidas"
                value={data.por_estado.atendido}
                sub={`${pct(data.por_estado.atendido)}%`}
                colorClass="bg-emerald-100 text-emerald-700"
              />
              <KpiCard
                icon={XCircle}
                label="Canceladas"
                value={data.por_estado.cancelada}
                sub={`${pct(data.por_estado.cancelada)}%`}
                colorClass="bg-rose-100 text-rose-700"
              />
              <KpiCard
                icon={UserX}
                label="No asistió"
                value={data.por_estado.no_asistio}
                sub={`${pct(data.por_estado.no_asistio)}%`}
                colorClass="bg-slate-100 text-slate-600"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Estado breakdown */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Distribución por estado
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {Object.entries(data.por_estado).map(([key, count]) => {
                    const state = APPOINTMENT_STATES[key];
                    if (!state) return null;
                    const p = pct(count);
                    return (
                      <div key={key} className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <Badge variant="outline" className={cn('text-xs', state.color)}>
                            {state.label}
                          </Badge>
                          <span className="font-medium">{count} ({p}%)</span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${p}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {data.total === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">Sin datos en este período.</p>
                  )}
                </CardContent>
              </Card>

              {/* Hora pico */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Horas con más citas
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {data.hora_pico.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">Sin datos.</p>
                  ) : (
                    data.hora_pico.map((row, i) => {
                      const maxCount = data.hora_pico[0]?.count || 1;
                      return (
                        <div key={row.hora} className="flex items-center gap-3">
                          <span className="text-xs font-mono w-12 shrink-0 text-muted-foreground">{row.hora}</span>
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-cyan-500 rounded-full"
                              style={{ width: `${(row.count / maxCount) * 100}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium w-6 text-right">{row.count}</span>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>

              {/* Top doctores */}
              {data.top_doctores.length > 0 && (
                <Card className="md:col-span-2">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Citas por doctor
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {data.top_doctores.map(doc => {
                        const p = pct(doc.total);
                        return (
                          <div key={doc.doctor_id} className="flex items-center gap-3">
                            <div
                              className="w-3 h-3 rounded-full shrink-0"
                              style={{ backgroundColor: doc.color || '#0ea5e9' }}
                            />
                            <span className="text-sm flex-1 truncate">{doc.nombre}</span>
                            <div className="w-32 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full"
                                style={{ width: `${p}%`, backgroundColor: doc.color || '#0ea5e9' }}
                              />
                            </div>
                            <span className="text-xs font-medium w-16 text-right">{doc.total} ({p}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};
